import React, { useEffect, useRef, useState } from 'react';

import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { Skeleton } from '@material-ui/lab';
import { Typography } from '@material-ui/core';
import { ErrorOutline } from '@material-ui/icons';
import { maxBy, minBy } from 'lodash';

function render(status) {
    if (status === Status.FAILURE) {
        return (
            <div
                style={{
                    height: '350px',
                    borderRadius: '5px',
                    backgroundColor: 'rgb(50,50,50)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <ErrorOutline />
                <Typography>There was an error loading the map</Typography>
            </div>
        );
    }

    // else status === Status.LOADING
    return <Skeleton variant="rect" height="350px" />;
}

function _GoogleMap({ forwardRef, ...props }) {
    const { className, onClick, style, children, onBoundsChange, ...options } = props;

    const ref = useRef(null);
    const map = useRef(null);
    const prevBoundsRef = useRef(null);
    const prevBoundsKeyRef = useRef(null);
    const isMountedRef = useRef(true);
    // Latest onClick without re-subscribing the map listener; consumers pass
    // inline handlers whose identity changes every render.
    const onClickRef = useRef(onClick);
    onClickRef.current = onClick;
    // Re-render once the map instance exists so children (markers) get re-cloned
    // with the real `map.current`. Without this, children rendered synchronously
    // (before the map-creation effect runs) are cloned with `map: null` and never
    // attach to the map.
    const [mapReady, setMapReady] = useState(false);

    // Track mounted state to prevent async callbacks from updating destroyed maps
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // The map is only ever created once, from first-render options; later option
    // changes flow through the dedicated effects below.
    useEffect(() => {
        if (ref.current && !map?.current && options) {
            const gmap = new window.google.maps.Map(ref.current, options);
            map.current = gmap;

            if (forwardRef) {
                forwardRef.current = gmap;
            }

            setMapReady(true);
        }
    }, []);

    useEffect(() => {
        if (!options.bounds || !map?.current || !Array.isArray(options.bounds) || options.bounds.length === 0) {
            return;
        }

        // Cheap bails before any bounds math: same array identity, then same
        // serialized content.
        if (options.bounds === prevBoundsRef.current) {
            return;
        }

        const boundsKey = JSON.stringify(options.bounds);
        if (boundsKey === prevBoundsKeyRef.current) {
            prevBoundsRef.current = options.bounds;
            return;
        }

        let boundsOverride = [...options.bounds];
        const [maxLat, maxLng] = ['lat', 'lng'].map((attr) => maxBy(boundsOverride, (b) => b[attr]));
        const [minLat, minLng] = ['lat', 'lng'].map((attr) => minBy(boundsOverride, (b) => b[attr]));

        if (maxLat.lat - minLat.lat < 1) {
            const padd = (1 - (maxLat.lat - minLat.lat)) / 2;
            boundsOverride.push({ lat: maxLat.lat + padd, lng: maxLat.lng });
            boundsOverride.push({ lat: minLat.lat - padd, lng: minLat.lng });
        }
        if (maxLng.lng - minLng.lng < 1) {
            const padd = (1 - (maxLng.lng - minLng.lng)) / 2;
            boundsOverride.push({ lat: maxLng.lat, lng: maxLng.lng + padd });
            boundsOverride.push({ lat: minLng.lat, lng: minLng.lng - padd });
        }

        const mapBounds = new window.google.maps.LatLngBounds();
        boundsOverride.forEach((coords) => {
            mapBounds.extend(coords);
        });

        prevBoundsRef.current = options.bounds;
        prevBoundsKeyRef.current = boundsKey;
        map.current.fitBounds(mapBounds);
    }, [options.bounds, map]);

    // The map is built once from first-render options, so a maxZoom that turns on or off later has
    // to be pushed through setOptions. `?? null` rather than passing undefined straight through:
    // null is what clears an existing cap.
    useEffect(() => {
        if (!map?.current) {
            return;
        }

        map.current.setOptions({ maxZoom: options.maxZoom ?? null });
    }, [mapReady, options.maxZoom]);

    useEffect(() => {
        let listener;
        if (map?.current && onBoundsChange) {
            listener = window.google.maps.event.addListener(map.current, 'bounds_changed', () => {
                if (!isMountedRef.current || !map.current) return;
                const bounds = map.current.getBounds();

                onBoundsChange(bounds);
            });
        }

        return () => {
            if (listener && window.google?.maps?.event) {
                window.google.maps.event.removeListener(listener);
            }
        };
    }, [map, onBoundsChange]);

    // Stable click subscription: attach once when the map exists and forward to
    // the latest onClick through a ref, instead of re-subscribing per render.
    useEffect(() => {
        if (map?.current) {
            // Clear listeners
            ['click'].forEach((eventName) => window.google.maps.event.clearListeners(map.current, eventName));
            map.current.addListener('click', (e) => onClickRef.current?.(e));
        }
    }, [mapReady]);

    useEffect(() => {
        const { center, zoom } = options;
        if (map?.current && (center || zoom)) {
            if (center === undefined) {
                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            // Check if component is still mounted before updating
                            if (!isMountedRef.current || !map.current) return;
                            const pos = {
                                lat: position.coords.latitude,
                                lng: position.coords.longitude,
                            };

                            map.current.setOptions({ ...options, center: pos, zoom });
                        },
                        () => {
                            // Check if component is still mounted before updating
                            if (!isMountedRef.current || !map.current) return;
                            // geo location declined, default to zoomed out Denver
                            map.current.setOptions({ ...options, center: { lat: 39.74, lng: -104.99 }, zoom: 5 });
                        }
                    );
                } else {
                    map.current.setOptions({ ...options, center: { lat: 0, lng: 0 } });
                }
            } else {
                map.current.setOptions(options);
            }
        }
    }, [map.current, options.center, options.zoom]);

    return (
        <>
            <div className={`map-container ${className}`} ref={ref} style={style} />
            {React.Children.map(children, (child) => {
                if (React.isValidElement(child)) {
                    if (child.props.children && child.type === Symbol.for('react.fragment')) {
                        return (
                            <React.Fragment>
                                {Array.isArray(child.props.children)
                                    ? child.props.children
                                          .filter((_child) => _child !== null)
                                          .map((_child) => {
                                              return React.cloneElement(_child, {
                                                  map: map.current,
                                              });
                                          })
                                    : React.cloneElement(child.props.children, {
                                          map: map.current,
                                      })}
                            </React.Fragment>
                        );
                    }
                    return React.cloneElement(child, { map: map.current });
                }
            })}
        </>
    );
}

export { default as Marker } from './Marker';
export { default as Circle } from './Circle';
export { default as DirectionsRenderer } from './DirectionsRenderer';
export { default as InfoWindow } from './InfoWindow';

function GoogleMap({ forwarded, defaultMap, ...rest }) {
    return (
        <Wrapper render={render}>
            <_GoogleMap
                {...rest}
                draggableCursor="pointer"
                gestureHandling="greedy"
                {...(defaultMap ? {} : { mapId: 'd3247097a38c69d0' })}
                zoomControlOptions={{
                    position: window.google.maps.ControlPosition.LEFT_BOTTOM,
                }}
                streetViewControlOptions={{
                    position: window.google.maps.ControlPosition.LEFT_BOTTOM,
                }}
                forwardRef={forwarded}
            />
        </Wrapper>
    );
}

export default React.forwardRef((props, ref) => {
    return <GoogleMap {...props} forwarded={ref} />;
});
